import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Template : famille "modèle de fiche".
 *
 * Enregistre une fiche existante comme MODÈLE, pour en créer d'autres ensuite :
 * l'utilisateur part d'un projet type ("chantier standard", avec ses trois
 * tâches d'ouverture) et en refait un en un geste. Le modèle vit au socle, pas
 * dans une app : il vaut pour TOUTES les familles.
 *
 * Famille à part, collection `templates`, pour ne toucher à aucune famille
 * existante : un modèle de projet n'est pas un projet (il n'a ni budget, ni
 * échéance, ni état d'avancement), et le ranger dans `projects` polluerait
 * toutes les listes de projets de toutes les orgs.
 *
 * CE QUE `type` PORTE ICI, et c'est la seule bizarrerie de cette famille : la
 * FAMILLE CIBLE, pas un sous-type métier. Le discriminant du socle sert de
 * classement par famille visée, et `targetType` porte le sous-type que les
 * fiches créées recevront. Détail plus bas, sur les deux champs.
 *
 * UN MODÈLE PORTE AUSSI SES FICHES LIÉES (`children`) : la caméra type vient
 * avec ses deux interventions d'entrée, et créer depuis ce modèle les crée
 * toutes les trois. Deux choses s'y jouent, détaillées sur le champ : chaque
 * enfant a un `id` STABLE dans le modèle (c'est ce qui rend sa case adressable
 * à l'écran), et son `linkKey` est un CHEMIN DE STOCKAGE, pas une simple clef.
 *
 * Convention de patron (cf. asset, project) :
 *   - `xCoreFields`  : les champs métier seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/**
 * Les champs d'UN ENFANT, hors son `id` : le patron de l'enfant, partagé par
 * la forme d'ENTRÉE (id facultatif) et la forme LUE (id requis), plus bas.
 */
const templateChildCoreFields = {
  /** La clef du repository cible dans `TakiSdk`, au pluriel ("tasks", "careRecords"). */
  family: z.string().min(1),
  /** Le sous-type que portera la fiche créée ("preparation", "intervention"). */
  type: z.string().min(1),
  /**
   * CHEMIN DE STOCKAGE du champ de l'enfant qui reçoit l'id de la fiche
   * racine créée : `projectId` à la racine de la fiche, ou
   * `customFields.materielId` dans le sac de l'app. C'est le `par` du
   * lien déclaré par la porte (`taki.screens.json`), le même mot des deux
   * côtés.
   *
   * UN CHEMIN, PAS UNE CLEF, et ça se trompe en silence : un lien rangé
   * dans `customFields` qu'on écrirait à plat sur la fiche donnerait un
   * enfant que la carte liée ne montre jamais, sans une seule erreur
   * (cf. `poserParChemin`, packages/sdk/src/templates.ts).
   */
  linkKey: z.string().min(1),
  /**
   * Les champs MÉTIER de l'enfant, `customFields` compris. Le champ de
   * lien n'y est PAS : il est reposé à l'instanciation, sur la racine
   * réellement créée. Un modèle est un patron, il ne retient aucun lien
   * vers une fiche précise.
   */
  fields: z.record(z.string(), z.unknown()),
} as const;

/**
 * Un enfant tel que l'APPELANT le fournit (`create`, `update`, `fromRecord`) :
 * son `id` est FACULTATIF, le SDK le pose avant d'écrire (`avecIdsDEnfants`,
 * packages/sdk/src/templates.ts).
 */
export const templateChildInputSchema = z.object({
  id: z.string().min(1).optional(),
  ...templateChildCoreFields,
});

/**
 * Un enfant tel qu'il est LU : son `id` est REQUIS, et c'est ce qui rend une
 * case d'enfant adressable à l'écran (édition en place, pile d'annulation,
 * sélection pour un retrait) ; sans lui rien ne distingue deux enfants
 * identiques.
 *
 * LES MODÈLES ÉCRITS AVANT CE CHANTIER N'EN PORTENT PAS, et ce schéma ne les
 * rend pas illisibles pour autant : c'est le convertisseur de lecture du SDK
 * qui pose les ids manquants AVANT de parser (`lireModele`,
 * packages/sdk/src/templates.ts), et la prochaine écriture les persiste. Le
 * type `Template` peut donc promettre `id: string`, ce que le port du socle
 * exige (`EnfantDeModele`, packages/ui/src/views/contract.ts).
 *
 * Ce n'est PAS l'id d'une fiche : aucune fiche n'existe tant que le modèle
 * n'est pas instancié, et il ne part jamais à la création.
 */
export const templateChildSchema = z.object({
  id: z.string().min(1),
  ...templateChildCoreFields,
});

/** Type d'un enfant fourni par l'appelant (id facultatif). */
export type TemplateChildInput = z.infer<typeof templateChildInputSchema>;

/** Type d'un enfant lu (id requis). */
export type TemplateChild = z.infer<typeof templateChildSchema>;

/** Champs métier de la famille template (hors champs standard du socle). */
export const templateCoreFields = {
  orgId: z.string(),
  /**
   * Discriminant de la famille, qui porte ici LA FAMILLE CIBLE, sous la forme
   * de la clef du repository dans `TakiSdk` : "projects", "tasks", "assets".
   * C'est le vocabulaire de `taki.fields.json` (clef `family`), le même mot
   * partout.
   *
   * C'est voulu, et c'est ce qui rend la famille utilisable sans rien ajouter
   * au socle de lecture : `list({ type: 'projects' })` rend les modèles de
   * projets CÔTÉ SERVEUR, avec le seul filtre que le SDK sait faire.
   */
  type: z.string().min(1),
  /** Le `type` que porteront les fiches créées depuis ce modèle (le sous-type de la porte, ex. "chantier"). */
  targetType: z.string().min(1),
  /** Nom du modèle, tel qu'il s'affiche dans la liste des modèles. */
  name: z.string().min(1),
  /** Ce à quoi le modèle sert, en une phrase. Optionnel. */
  description: z.string().optional(),
  /**
   * Les champs MÉTIER à recopier sur la fiche racine, `customFields` compris,
   * sous forme de map. JAMAIS un champ standard (id, dates, auteurs,
   * archivedAt, deletedAt, schemaVersion) : ceux-là sont posés par le SDK à
   * chaque création, les recopier écrirait l'histoire de la fiche d'origine.
   */
  fields: z.record(z.string(), z.unknown()),
  /**
   * Les fiches ENFANTS à créer avec la racine (les tâches d'ouverture d'un
   * chantier, les interventions d'entrée d'une caméra). `family` est la clef du
   * repository cible, `type` le sous-type de l'enfant. Optionnel : un modèle
   * sans enfant est le cas courant.
   */
  children: z.array(templateChildInputSchema).optional(),
  /** Id de la fiche dont le modèle a été tiré. Informatif : la fiche peut disparaître, le modèle reste. Optionnel. */
  sourceId: z.string().min(1).optional(),
} as const;

/**
 * Schéma complet de la famille template (champs standard + champs métier).
 * `children` y est REDÉFINI dans sa forme LUE (id requis, cf.
 * `templateChildSchema`) : le type `Template` promet un id par enfant, les
 * schémas d'ENTRÉE ci-dessous gardent la forme à id facultatif.
 */
export const TemplateSchema = z.object({
  ...baseEntityFields,
  ...templateCoreFields,
  children: z.array(templateChildSchema).optional(),
  /**
   * LES PLANS RÉCURRENTS QUI CITENT CE MODÈLE (leurs ids), en LECTURE seule :
   * aucun schéma d'entrée ne la porte. Le SDK la tient dans le MÊME lot que
   * l'écriture du plan (`arrayUnion` à la création, `arrayRemove` à la
   * suppression, packages/sdk/src/recurrences.ts), et les Security Rules
   * vérifient les deux côtés (`getAfter`) : un id n'y entre qu'avec un plan
   * qui pointe ce modèle, et n'en sort qu'avec la suppression de ce plan.
   *
   * C'est le verrou du modèle : tant qu'elle n'est pas vide, ni archiver ni
   * supprimer ne passe, sinon un plan vivant produirait la nuit depuis un
   * modèle absent. ARCHIVER UN PLAN NE LE LIBÈRE PAS : un plan archivé se
   * désarchive, et retrouverait un modèle supprimé. Un modèle d'avant ce
   * chantier n'en porte pas : il se lit vide.
   */
  plansIds: z.array(z.string()).default([]),
});

/** Type d'un template. */
export type Template = z.infer<typeof TemplateSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const TemplateCreateInputSchema = z.object({
  ...templateCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un template. */
export type TemplateCreateInput = z.infer<typeof TemplateCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const TemplateUpdateInputSchema = z
  .object({ ...templateCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un template. */
export type TemplateUpdateInput = z.infer<typeof TemplateUpdateInputSchema>;
